import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { FC, Fragment } from 'react';

import { useTranslation } from '@gem/modules/i18n';
import { PopupStore } from '@gem/popups';
import { Button, Divider, IconView, MenuItem } from '@gem/ui-kit';

import { AvatarTypes } from 'APP/components/Avatar/Avatar';
import { UserAvatar } from 'APP/components/Avatar/UserAvatar/UserAvatar';
import { ProfileNameBadge } from 'APP/components/ProfileNameBadge/ProfileNameBadge';
import { ProfileEmail } from 'APP/main/Profile/components/ProfileEmail/ProfileEmail';
import { ProfileNotifications } from 'APP/main/Profile/components/ProfileNotifications/ProfileNotifications';
import { ProfilePosition } from 'APP/main/Profile/components/ProfilePosition/ProfilePosition';

import { PhoneNumber } from '../components/PhoneNumber/PhoneNumber';
import { ProfileNickname } from '../components/ProfileNickname/ProfileNickname';
import { isDivider, useMyProfilePresenter } from './MyProfile.presenter';
import styles from './MyProfile.styles.m.css';

interface IMyProfileProps {
  className?: string;
  metaBlockClassName?: string;
  popupInstance?: PopupStore;
}

export const MyProfile: FC<IMyProfileProps> = observer(
  ({ className, metaBlockClassName, popupInstance }) => {
    const { t } = useTranslation();
    const presenter = useMyProfilePresenter(popupInstance);

    return (
      <div className={className}>
        <div className={styles.header}>
          <UserAvatar
            className={styles.avatar}
            id={presenter.me.id}
            type={AvatarTypes.Round_30}
            withFullScreen={true}
          />
          <div className={styles.info}>
            <h3
              className={styles.title}
              data-test="username"
            >
              {presenter.me.displayName}
              <ProfileNameBadge
                inlineBadge
                source={presenter.userNameBadge?.source}
                animated={presenter.userNameBadge?.animated}
                displayUILayer={1}
              />
            </h3>
            <Button
              className={styles.actionButton}
              onClick={presenter.openEditProfile}
              theme="secondary"
            >
              {t('edit_profile')}
            </Button>
          </div>
        </div>

        {presenter.me.position && (
          <ProfilePosition
            className={classNames(styles.contact, metaBlockClassName)}
            position={presenter.me.position}
          />
        )}
        <ProfileNickname
          className={classNames(styles.contact, metaBlockClassName)}
          user={presenter.me}
        />
        {presenter.me.displayPhone && (
          <PhoneNumber
            className={classNames(styles.contact, metaBlockClassName)}
            phoneNumber={presenter.me.displayPhone}
          />
        )}

        <ProfileEmail
          className={classNames(styles.contact, metaBlockClassName)}
          email={presenter.me.email}
          isSendMail={false}
        />

        <Divider theme="style1" />

        <ProfileNotifications />

        {presenter.menuItems.map((menuItem) => (
          <Fragment key={menuItem.key}>
            {isDivider(menuItem) && <Divider theme="style1" />}

            {!isDivider(menuItem) && (
              <MenuItem
                className={styles.listItem}
                title={menuItem.title}
                count={menuItem.count}
                icon={
                  <IconView
                    Icon={menuItem.Icon}
                    className={styles.listIcon}
                  />
                }
                onClick={menuItem.onClick}
                suffix={<span className={styles.languageSuffix}>{menuItem.suffix}</span>}
              />
            )}
          </Fragment>
        ))}
      </div>
    );
  }
);
